import { CloseOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_CHANNEL_IDEA,
  GET_IDEA_MEMBERS,
  GET_IDEA_RESULTS_BY_ID,
  GET_ME,
  Idea,
  Me,
  Permission,
  User,
} from '@frontend/data-access';
import { IdeaChannelReviewers } from '@frontend/feature-idea-channel';
import { UserAvatar } from '@frontend/shared/ui';
import { formatDate } from '@frontend/shared/util';
import { Avatar, Button, Divider, List, Modal } from 'antd';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import IdeaChannelEvaluationResultTag from '../idea-channel-evaluation-result-tag/idea-channel-evaluation-result-tag';
import IdeaEvaluationResult from '../idea-evaluation-result/idea-evaluation-result';
import IdeaChannelPdfDownload from './pdf-download';
import PhaseActionButton from './phase-action';
import IdeaChannelTagList from './tag-list';
import useTranslation from 'next-translate/useTranslation';

type ActionType = 'REVIEWERS' | 'SCORE';

export const IdeaBasics = ({
  ideaId,
  isPrinting = false,
}: {
  ideaId: string;
  isPrinting?: boolean;
}) => {
  const { t } = useTranslation('common');
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);

  const [getIdea, { data, refetch }] = useLazyQuery<{ channelIdea: Idea }>(
    GET_CHANNEL_IDEA
  );
  const [getEvaluationResult, { data: resultData }] = useLazyQuery<{
    channelIdea: Idea;
  }>(GET_IDEA_RESULTS_BY_ID);

  const [actionType, setActionType] = useState<ActionType>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [getIdeaMembers, { data: ideaMembers, loading: ideaMembersLoading }] =
    useLazyQuery<{
      getIdeaMembers: {
        user: User;
        permission: Permission;
      }[];
    }>(GET_IDEA_MEMBERS, {
      fetchPolicy: 'no-cache',
    });

  const channelReviewers =
    ideaMembers?.getIdeaMembers.filter(
      (item) => item.permission.resource.name === 'CHANNEL_REVIEWER'
    ) || [];
  const ideaReviewers =
    ideaMembers?.getIdeaMembers.filter(
      (item) => item.permission.resource.name === 'IDEA_REVIEWER'
    ) || [];
  const ideaExperts =
    ideaMembers?.getIdeaMembers.filter(
      (item) => item.permission.resource.name === 'CHANNEL_EXPERT'
    ) || [];

  const handleCloseModal = () => {
    setActionType(undefined);
    setIsModalOpen(false);
  };

  const renderModalTitle = () => {
    switch (actionType) {
      case 'REVIEWERS':
        return t('edit_reviewers');
      case 'SCORE':
        return t('expert_score');
      default:
        return null;
    }
  };
  const renderModal = () => {
    switch (actionType) {
      case 'REVIEWERS':
        return <IdeaChannelReviewers ideaId={ideaId} />;
      case 'SCORE':
        return <IdeaEvaluationResult ideaId={ideaId} />;
      default:
        return null;
    }
  };
  const getModalWidth = () => {
    switch (actionType) {
      case 'REVIEWERS':
        return 800;
      case 'SCORE':
        return 1200;
      default:
        return 800;
    }
  };

  const handleOpenModal = (action: ActionType) => {
    setActionType(action);
    setIsModalOpen(true);
  };

  const isEditAccess = () => {
    return !!(
      meData?.me.role === 'ADMIN' ||
      [...channelReviewers, ...ideaReviewers].find(
        (u) => u.user.id === meData?.me?.id
      )
    );
  };

  const reviewerItems = [
    {
      title: (
        <div className="flex justify-between items-center">
          {t('reviewers')}
        </div>
      ),
      content: (
        <div>
          {[...ideaReviewers, ...channelReviewers].length ? (
            <List
              size="small"
              loading={ideaMembersLoading}
              dataSource={[...ideaReviewers, ...channelReviewers]}
              split={false}
              renderItem={(item) => (
                <List.Item className="!p-0 mb-2">
                  <div className="flex gap-2">
                    <UserAvatar user={item.user} size={32} />
                    <div className="text-sm max-w-56">
                      <div className="text-gray-900 truncate">
                        {item.user.fullName}
                      </div>
                      <div className="text-gray-700 truncate">
                        {item.user.email}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Avatar icon={<UserOutlined />} size={32} /> {t('unassigned')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <div className="flex justify-between items-center">{t('experts')}</div>
      ),
      content: (
        <div>
          {ideaExperts?.length ? (
            <List
              size="small"
              loading={ideaMembersLoading}
              dataSource={ideaExperts}
              split={false}
              renderItem={(item) => (
                <List.Item className="!p-0 mb-2">
                  <div className="flex gap-2">
                    <UserAvatar user={item.user} size={32} />
                    <div className="text-sm max-w-56">
                      <div className="text-gray-900 truncate">
                        {item.user.fullName}
                      </div>
                      <div className="text-gray-700 truncate">
                        {item.user.email}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div className="flex items-center gap-2">
              <Avatar icon={<UserOutlined />} size={32} /> {t('unassigned')}
            </div>
          )}
        </div>
      ),
    },
  ];

  const ideaRelatedItems = [
    {
      title: t('idea_channel'),
      content: data?.channelIdea.channel?.title,
    },
    {
      title: t('idea_detail_basics_innovation_space'),
      content: data?.channelIdea.innovationSpaceTag?.name,
    },
    {
      title: t('idea_detail_basics_challenge'),
      content: (
        <Link
          data-test="idea-challenge-name"
          className="text-primary-600"
          target="_blank"
          href={
            '/intrapreneurship/challenge/' + data?.channelIdea.challenge?.id ||
            ''
          }
        >
          {data?.channelIdea.challenge?.title}
        </Link>
      ),
    },
    {
      title: t('created_date'),
      content: (
        <div>
          {data?.channelIdea.createdAt &&
            formatDate(data.channelIdea.createdAt, 'DD MMM YYYY, HH:mm')}
        </div>
      ),
    },
    {
      title: t('strategic_driver'),
      content: data?.channelIdea.drivers?.length ? (
        <div>
          {data?.channelIdea.drivers?.map((driver) => driver.driver).join(', ')}
        </div>
      ) : null,
    },
    {
      title: t('idea_detail_basics_duration'),
      content: data?.channelIdea.duration ? (
        <div>{data?.channelIdea.duration}</div>
      ) : null,
    },
  ];

  const basicItems = [
    {
      title: t('Region'),
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {data?.channelIdea.regions?.map((item, index) => {
            return (
              <div
                key={index}
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
              >
                {item.name}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: t('target_country'),
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {data?.channelIdea.countries?.map((item, index) => {
            return (
              <div
                key={index}
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
              >
                {item.name}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: t('Topics'),
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {data?.channelIdea.topics?.map((item, index) => {
            return (
              <div
                key={index}
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
              >
                {item.name}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      title: t('business_functional_area'),
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {data?.channelIdea.businessAreas?.map((item, index) => {
            return (
              <div
                key={index}
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
              >
                {item}
              </div>
            );
          })}
        </div>
      ),
    },
    // {
    //   title: 'Idea status',
    //   content: (
    //     <div>
    //       {data?.channelIdea.state &&
    //         getLinkLabel(data.channelIdea.state.toLowerCase())}
    //     </div>
    //   ),
    // },
  ];

  useEffect(() => {
    if (ideaId) {
      getIdea({ variables: { ideaId: ideaId } });
      getEvaluationResult({ variables: { ideaId: ideaId } });
      getIdeaMembers({
        variables: {
          ideaId: ideaId,
        },
      });
    }
  }, [getEvaluationResult, getIdea, getIdeaMembers, ideaId]);

  if (data)
    return (
      <>
        <div className="border-l border-gray-400 !p-6">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <PhaseActionButton idea={data?.channelIdea} />
            {!isPrinting && <IdeaChannelPdfDownload idea={data?.channelIdea} />}
          </div>
          <Divider />
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="text-gray-700 text-sm font-normal">
                {t('idea_detail_expert_score_title')}
              </div>
              {(resultData?.channelIdea.channelCriteriaResults
                ?.length as number) > 0 && (
                <Button type="link" onClick={() => handleOpenModal('SCORE')}>
                  {t('idea_detail_see_detail')}
                </Button>
              )}
            </div>
            {resultData?.channelIdea.channelCriteriaResults?.length ? (
              <IdeaChannelEvaluationResultTag
                results={resultData.channelIdea.channelCriteriaResults}
              />
            ) : (
              <div className="text-gray-700 text-sm font-medium">
                {t('no_information')}
              </div>
            )}
          </div>
          <Divider />
          <div className="space-y-[14px]">
            {reviewerItems.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="text-gray-700 text-sm font-normal">
                  {item.title}
                </div>
                <div className="text-gray-900 text-sm font-medium">
                  {item.content}
                </div>
              </div>
            ))}
            {isEditAccess() && (
              <Button
                className="flex"
                type="link"
                onClick={() => handleOpenModal('REVIEWERS')}
                icon={<EditOutlined />}
                data-test="edit-reviewers-btn"
              >
                {t('edit_reviewer')}
              </Button>
            )}
          </div>
          <Divider />
          <div className="space-y-2">
            <div className="text-gray-700 text-sm font-normal">
              {t('idea_tags')}
            </div>
            <IdeaChannelTagList idea={data?.channelIdea} onFinish={refetch} />
          </div>
          <Divider />
          <div className="space-y-[14px]">
            {ideaRelatedItems
              .filter((item) => !!item.content)
              .map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="text-gray-700 text-sm font-normal">
                    {item.title}
                  </div>
                  <div className="text-gray-900 text-sm font-medium">
                    {item.content}
                  </div>
                </div>
              ))}
          </div>
          <Divider />
          <div className="space-y-[14px]">
            {basicItems.map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="text-gray-700 text-sm font-normal">
                  {item.title}
                </div>
                <div className="text-gray-900 text-sm font-medium">
                  {item.content}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Modal
          closeIcon={<CloseOutlined />}
          open={isModalOpen}
          onCancel={handleCloseModal}
          title={renderModalTitle()}
          width={getModalWidth()}
          footer={false}
        >
          {renderModal()}
        </Modal>
      </>
    );
  return <div />;
};
